// Delete with Undo: the row (and the rows hanging off it) is read first, deleted, and for UNDO_MS an "Undo" shows
// (src/components/UndoToast.jsx, in the shell). Undo puts the same rows back - same id, so links to it still work.
// The database client is passed in (pages pass supabase), so the tests run it without a network.
// Not for records whose delete also changes other things the Undo cannot see (an APA ledger, a purchase order, a
// bunkering with its ORB entry) or for crew (select=* refused): those keep a plain confirm.

export const UNDO_MS = 10000

let entries = []          // { id, label, until, undo }
const subs = new Set()
const emit = () => subs.forEach(f => { try { f(entries) } catch (e) {} })
let seq = 0

export function subscribeUndo(fn) { subs.add(fn); fn(entries); return () => subs.delete(fn) }
export const undoEntries = () => entries

// show "Deleted X - Undo" for UNDO_MS; returns its id
export function offerUndo({ label, undo }, now = Date.now()) {
  const id = ++seq
  entries = [...entries, { id, label: String(label || 'Record'), until: now + UNDO_MS, undo }]
  emit()
  setTimeout(() => dropUndo(id), UNDO_MS).unref?.()
  return id
}
export function dropUndo(id) { if (entries.some(e => e.id === id)) { entries = entries.filter(e => e.id !== id); emit() } }
// Undo pressed: once only; too late = false
export async function runUndo(id, now = Date.now()) {
  const e = entries.find(x => x.id === id)
  dropUndo(id)
  if (!e || now > e.until) return false
  await e.undo()
  return true
}

const errOf = r => r && r.error ? new Error(r.error.message || String(r.error)) : null

// table: database table; id: the row; children: [{ table, column }] rows pointing at it (snapshotted, put back too;
// deleteChildren: deleted first, for tables without ON DELETE CASCADE); onUndone(row): reload the page after an Undo
export async function deleteWithUndo(db, { table, id, label, children = [], deleteChildren = false, onUndone }) {
  const snap = await db.from(table).select('*').eq('id', id)
  if (errOf(snap)) throw errOf(snap)
  const row = (snap.data || [])[0] || null
  const kids = []
  for (const c of children) {
    const r = await db.from(c.table).select('*').eq(c.column, id)
    if (!errOf(r) && r.data && r.data.length) kids.push({ ...c, rows: r.data })
  }
  if (deleteChildren) for (const c of children) { const r = await db.from(c.table).delete().eq(c.column, id); if (errOf(r)) throw errOf(r) }
  const del = await db.from(table).delete().eq('id', id).select('id')
  if (errOf(del)) throw errOf(del)
  if (!del.data || !del.data.length) throw new Error('nothing was deleted - this login may not delete it, or it was already gone.')
  if (row) offerUndo({
    label,
    undo: async () => {
      const r = await db.from(table).insert(row)
      if (errOf(r)) throw errOf(r)
      // children the database removed with it come back; ones still there are left alone
      for (const k of kids) { const x = await db.from(k.table).upsert(k.rows, { onConflict: 'id', ignoreDuplicates: true }); if (errOf(x)) throw errOf(x) }
      if (onUndone) onUndone(row)
    },
  })
  return row
}
